import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency } from "./index";

/**
 * Enterprise PDF Engine for iShopMaster
 * Professional gradient headers, intelligent layout, purchase tracking
 */
export const PDFFormatter = {
    COLORS: {
        PRIMARY: [0, 28, 58],
        PRIMARY_LIGHT: [25, 50, 80],
        ACCENT: [105, 108, 255],
        TEXT_DARK: [30, 30, 30],
        TEXT_MUTED: [100, 100, 100],
        TEXT_LIGHT: [255, 255, 255],
        BORDER: [220, 220, 220],
        WATERMARK: [245, 245, 245]
    },

    DIMENSIONS: {
        HEADER_HEIGHT: 50,
        FOOTER_HEIGHT: 25,
        MARGIN: 15,
        LOGO_MAX_WIDTH: 35,
        LOGO_MAX_HEIGHT: 22
    },

    _initDoc: () => {
        return new jsPDF({
            orientation: "p",
            unit: "mm",
            format: "a4"
        });
    },

    /**
     * Draw gradient header background
     */
    _drawGradientHeader: (doc, pageWidth) => {
        const { HEADER_HEIGHT } = PDFFormatter.DIMENSIONS;

        for (let i = 0; i < HEADER_HEIGHT; i++) {
            const ratio = i / HEADER_HEIGHT;
            const r = PDFFormatter.COLORS.PRIMARY[0] + (PDFFormatter.COLORS.PRIMARY_LIGHT[0] - PDFFormatter.COLORS.PRIMARY[0]) * ratio;
            const g = PDFFormatter.COLORS.PRIMARY[1] + (PDFFormatter.COLORS.PRIMARY_LIGHT[1] - PDFFormatter.COLORS.PRIMARY[1]) * ratio;
            const b = PDFFormatter.COLORS.PRIMARY[2] + (PDFFormatter.COLORS.PRIMARY_LIGHT[2] - PDFFormatter.COLORS.PRIMARY[2]) * ratio;

            doc.setFillColor(r, g, b);
            doc.rect(0, i, pageWidth, 1, 'F');
        }
    },

    _drawWatermark: (doc, pageWidth, pageHeight) => {
        const prevColor = doc.getTextColor();
        const prevFontSize = doc.getFontSize();

        // Set low opacity
        doc.setGState(new doc.GState({ opacity: 0.1 }));
        doc.setTextColor(...PDFFormatter.COLORS.WATERMARK);
        doc.setFontSize(50);
        doc.setFont("helvetica", "bold");

        // Draw text while opacity is 0.1
        doc.text("OFFICIAL REQUEST", pageWidth / 2, pageHeight / 2, {
            align: "center",
            baseline: "middle",
            angle: 45
        });

        // Reset state for next elements
        doc.setGState(new doc.GState({ opacity: 1.0 }));
        doc.setTextColor(...prevColor);
        doc.setFontSize(prevFontSize);
    },

    /**
     * Intelligent logo placement with white padding
     */
    _drawLogoWithPadding: (doc, logoUrl, x, y) => {
        const { LOGO_MAX_WIDTH, LOGO_MAX_HEIGHT } = PDFFormatter.DIMENSIONS;

        try {
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(x - 2, y - 2, LOGO_MAX_WIDTH + 4, LOGO_MAX_HEIGHT + 4, 2, 2, 'F');
            doc.addImage(logoUrl, 'PNG', x, y, LOGO_MAX_WIDTH, LOGO_MAX_HEIGHT, undefined, 'FAST');
            return true;
        } catch (e) {
            console.warn("Logo failed to load:", e);
            return false;
        }
    },

    /**
     * Complete header with proper spacing to avoid overlap
     */
    _drawHeader: (doc, company, title, purchaseCount, pageWidth) => {
        const { MARGIN, HEADER_HEIGHT, LOGO_MAX_WIDTH } = PDFFormatter.DIMENSIONS;

        // 1. Gradient Background
        PDFFormatter._drawGradientHeader(doc, pageWidth);

        // 2. Logo (left side)
        const logoUrl = company?.company_logo || company?.logo;
        let logoDrawn = false;

        if (logoUrl) {
            logoDrawn = PDFFormatter._drawLogoWithPadding(doc, logoUrl, MARGIN, 10);
        }

        // 3. Company Brand Name (if no logo)
        if (!logoDrawn) {
            doc.setFontSize(20);
            doc.setTextColor(...PDFFormatter.COLORS.TEXT_LIGHT);
            doc.setFont("helvetica", "bold");
            doc.text((company?.company_name || 'iShopMaster').toUpperCase(), MARGIN, 20);
        }

        // 4. Company Details (Side of logo if logo exists)
        // Ensure infoX has enough room for LOGO_MAX_WIDTH (35) + padding (10)
        const infoX = logoUrl ? MARGIN + LOGO_MAX_WIDTH + 10 : MARGIN;
        const infoYStart = 15;

        doc.setFontSize(8);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_LIGHT);
        doc.setFont("helvetica", "bold");

        doc.text((company?.company_name || "").toUpperCase(), infoX, infoYStart);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const address = company?.address || "Enterprise Solutions";
        const addressLines = doc.splitTextToSize(address, pageWidth - infoX - MARGIN);
        doc.text(addressLines, infoX, infoYStart + 5);

        const contactLine = `Tel: ${company?.phone_number || ""} | Email: ${company?.email || ""}`;
        doc.text(contactLine, infoX, infoYStart + 7 + (addressLines.length * 3.5));

        // 5. Document Title (Right side)
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(title.toUpperCase(), pageWidth - MARGIN, 18, { align: "right" });

        // 6. Date (Right side)
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - MARGIN, 26, { align: "right" });

        // 7. Purchase Count (if provided and > 0)
        if (purchaseCount && purchaseCount > 0) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(`Purchase #${purchaseCount} with this supplier`, pageWidth - MARGIN, 32, { align: "right" });
        }

        // 8. Separator line
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(MARGIN, HEADER_HEIGHT - 2, pageWidth - MARGIN, HEADER_HEIGHT - 2);
    },

    _drawFooter: (doc, generatedBy, pageWidth, pageHeight) => {
        const { MARGIN } = PDFFormatter.DIMENSIONS;

        doc.setDrawColor(...PDFFormatter.COLORS.BORDER);
        doc.line(MARGIN, pageHeight - 15, pageWidth - MARGIN, pageHeight - 15);

        doc.setFontSize(8);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_MUTED);

        doc.setFont("helvetica", "bold");
        doc.text("www.ishopmaster.lk", pageWidth / 2, pageHeight - 10, { align: "center" });

        doc.setFont("helvetica", "normal");
        const userText = generatedBy ? `Generated by: ${generatedBy} | ` : "";
        doc.text(`${userText}Powered by iShopMaster ERP`, pageWidth / 2, pageHeight - 6, { align: "center" });
    },

    /**
     * Two-column FROM/TO layout (side by side)
     */
    _drawFromToColumns: (doc, fromData, toData, y, pageWidth) => {
        const midPoint = pageWidth / 2;

        // Left Column - FROM
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(15, y, midPoint - 18, 20, 2, 2, 'F');

        doc.setFontSize(7);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_MUTED);
        doc.setFont("helvetica", "bold");
        doc.text("FROM (OWNER):", 20, y + 6);

        doc.setFontSize(10);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_DARK);
        doc.setFont("helvetica", "bold");
        doc.text(fromData.name, 20, y + 12);

        doc.setFontSize(7);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_MUTED);
        doc.setFont("helvetica", "normal");
        doc.text(fromData.company, 20, y + 17);

        // Right Column - TO
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(midPoint + 3, y, midPoint - 18, 20, 2, 2, 'F');

        doc.setFontSize(7);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_MUTED);
        doc.setFont("helvetica", "bold");
        doc.text("TO (SUPPLIER):", midPoint + 8, y + 6);

        doc.setFontSize(10);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_DARK);
        doc.setFont("helvetica", "bold");
        doc.text(toData.name, midPoint + 8, y + 12);

        doc.setFontSize(7);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_MUTED);
        doc.setFont("helvetica", "normal");
        doc.text(toData.subtitle || "", midPoint + 8, y + 17);

        return y + 25;
    },

    /**
     * Personal message from shop owner to supplier
     */
    _drawPersonalMessage: (doc, ownerName, supplierName, y, pageWidth, purchaseCount, itemCount) => {
        const totalPurchases = purchaseCount + 1;
        const nextPurchaseText = purchaseCount > 0
            ? `marks our ${totalPurchases}${PDFFormatter._getOrdinal(totalPurchases)} purchase`
            : "is our first purchase request";

        const fromText = (doc.companyName && ownerName !== doc.companyName) ? ` from ${doc.companyName}` : "";

        doc.setFontSize(9);
        doc.setTextColor(...PDFFormatter.COLORS.TEXT_DARK);

        let currY = y;
        doc.setFont("helvetica", "bold");
        doc.text(`Dear ${supplierName},`, 15, currY);
        currY += 6;

        doc.setFont("helvetica", "normal");
        let intro = `I am `;
        let x = 15;
        doc.text(intro, x, currY);
        x += doc.getTextWidth(intro);

        doc.setFont("helvetica", "bold");
        doc.text(ownerName, x, currY);
        x += doc.getTextWidth(ownerName);

        doc.setFont("helvetica", "normal");
        doc.text(`${fromText}. We value our strong partnership.`, x, currY);
        currY += 6;

        doc.text("I am reaching out for a restock of ", 15, currY);
        x = 15 + doc.getTextWidth("I am reaching out for a restock of ");

        doc.setFont("helvetica", "bold");
        const countText = `${itemCount} items`;
        doc.text(countText, x, currY);
        x += doc.getTextWidth(countText);

        doc.setFont("helvetica", "normal");
        doc.text(`. This `, x, currY);
        x += doc.getTextWidth(". This ");

        doc.setFont("helvetica", "bold");
        doc.text(nextPurchaseText, x, currY);
        x += doc.getTextWidth(nextPurchaseText);

        doc.setFont("helvetica", "normal");
        doc.text(" with you.", x, currY);
        currY += 6;

        doc.text("Please provide your most competitive quotation for the inventory listed below.", 15, currY);

        return currY + 8;
    },

    _getOrdinal: (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return (s[(v - 20) % 10] || s[v] || s[0]);
    },

    applyGlobalBranding: (doc, company, title, generatedBy, purchaseCount) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        PDFFormatter._drawWatermark(doc, pageWidth, pageHeight);
        PDFFormatter._drawHeader(doc, company, title, purchaseCount, pageWidth);
        PDFFormatter._drawFooter(doc, generatedBy, pageWidth, pageHeight);
    },

    generatePurchaseInvoice: (purchase, company, user) => {
        const doc = PDFFormatter._initDoc();
        const title = "Purchase Receipt";
        const userName = user?.employeeId?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}` : (user?.username || "Admin"));

        autoTable(doc, {
            startY: 95,
            head: [['Item', 'Qty', 'Unit Price', 'Total']],
            body: (purchase.purchasedItems || []).map(it => [
                it.variant_id?.variantName || it.item_id?.itemName || it.itemName || "Unknown",
                it.purchaseQty || it.qty || 0,
                formatCurrency(it.unitCost || it.price || 0),
                formatCurrency(it.total_price || 0)
            ]),
            theme: 'grid',
            headStyles: { fillColor: PDFFormatter.COLORS.PRIMARY, textColor: 255, fontStyle: 'bold' },
            margin: { top: 55, bottom: 25 },
            didDrawPage: (data) => {
                PDFFormatter.applyGlobalBranding(doc, company, title, userName, null);

                if (data.pageNumber === 1) {
                    const ownerName = user?.employeeId?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}` : (company?.contact_person || company?.company_name || "Management"));
                    const sourceName = purchase.purchase_type === "Trade-In"
                        ? `${purchase.customer?.first_name || 'Customer'} ${purchase.customer?.last_name || ''}`
                        : (purchase.supplier?.business_name || "Walk-In");

                    let y = PDFFormatter._drawFromToColumns(doc,
                        { name: ownerName, company: (company?.company_name || "").toUpperCase() },
                        { name: sourceName, subtitle: `Ref: ${purchase.referenceNumber}` },
                        55,
                        doc.internal.pageSize.getWidth()
                    );
                }
            }
        });

        doc.save(`Invoice_${purchase.referenceNumber}.pdf`);
    },

    generateRFQ: (rfqData, company, user, allPurchases = []) => {
        const doc = PDFFormatter._initDoc();
        const title = "Official RFQ";
        const userName = user?.employeeId?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}` : (user?.username || "Admin"));
        const { supplierName, agentName, supplierId, items } = rfqData;

        // Calculate ACTUAL purchase count from purchases array
        const purchaseCount = allPurchases.filter(p =>
            p.supplier?._id === supplierId || p.supplier === supplierId
        ).length;

        autoTable(doc, {
            startY: 110,
            head: [['Item Description', 'Target Qty', 'Expected Unit Price']],
            body: items.map(it => [
                it.itemName,
                it.targetQty,
                (!it.unitCost || it.unitCost === 0) ? "REQUESTING BEST PRICE" : formatCurrency(it.unitCost)
            ]),
            theme: 'grid',
            headStyles: { fillColor: PDFFormatter.COLORS.PRIMARY, textColor: 255 },
            margin: { top: 60, bottom: 25 },
            didDrawPage: (data) => {
                PDFFormatter.applyGlobalBranding(doc, company, title, userName, purchaseCount);

                if (data.pageNumber === 1) {
                    const ownerName = user?.employeeId?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}` : (company?.contact_person || company?.company_name || "Management"));
                    doc.companyName = company?.company_name;

                    let y = PDFFormatter._drawFromToColumns(doc,
                        { name: ownerName, company: (company?.company_name || "").toUpperCase() },
                        { name: supplierName, subtitle: agentName ? `Attn: ${agentName}` : "" },
                        55,
                        doc.internal.pageSize.getWidth()
                    );

                    // Personal message with purchase and item counts
                    y = PDFFormatter._drawPersonalMessage(doc, ownerName, supplierName, y + 3, doc.internal.pageSize.getWidth(), purchaseCount, items.length);
                }
            }
        });

        doc.save(`RFQ_${supplierName.replace(/\s/g, '_')}.pdf`);
    },

    generateMasterRestockList: (groupedData, company, user) => {
        const doc = PDFFormatter._initDoc();
        const title = "Master Restock List";
        const userName = user?.employeeId?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}` : (user?.username || "Admin"));

        let currentY = 75;

        groupedData.forEach((group, groupIdx) => {
            if (currentY > 250) {
                doc.addPage();
                currentY = 75;
            }

            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...PDFFormatter.COLORS.PRIMARY);
            doc.text(`SUPPLIER: ${group.supplierName.toUpperCase()}`, 15, currentY);

            autoTable(doc, {
                startY: currentY + 2,
                head: [['Item', 'Current', 'Reorder', 'Order Qty']],
                body: group.items.map(it => [
                    it.itemName,
                    it.stockTracking?.availableForSale || 0,
                    it.stockTracking?.reorderPoint || 0,
                    it.targetQty || 0
                ]),
                theme: 'striped',
                headStyles: { fillColor: [80, 80, 80] },
                margin: { top: 55, bottom: 25 },
                didDrawPage: () => {
                    PDFFormatter.applyGlobalBranding(doc, company, title, userName, null);

                    doc.setFontSize(9);
                    doc.setTextColor(...PDFFormatter.COLORS.ACCENT);
                    doc.text("INTERNAL PROCUREMENT SUMMARY", doc.internal.pageSize.getWidth() / 2, 70, { align: "center" });
                }
            });

            currentY = doc.lastAutoTable.finalY + 10;
        });

        doc.save(`Master_Restock_${new Date().getTime()}.pdf`);
    }
};
